import {baseUrl} from "./ApiEndpoints";
import {ApiService} from "./http/FederateApi.service";
import { MockApiService } from "./MockApiService";
import { isLocalDevelopment } from "../config/local.config";

export class BookmarksApiService extends ApiService {

    private mockService: MockApiService;

    constructor() {
        super({
            baseUrl
        });
        this.mockService = new MockApiService();
    }
    // https://api.longzhiw.people.amazon.dev/bookmarks/
    private urls = {
        bookmarks: "/bookmarks/",
        bookmarksSearch: "/bookmarks/search/",
    }

    getBookmark(bookmarkId: string) {
        if (isLocalDevelopment()) {
            console.log(`Using mock service for get bookmark: ${bookmarkId}`);
            return this.mockService.getBookmarks().then(result => {
                const bookmark = result.favorite_bookmarks.find((b: any) => b.id === bookmarkId);
                return bookmark || null;
            });
        }
        
        return super.get(`${this.urls.bookmarks}${bookmarkId}`)
            .then(response => {
                console.log(`API Response raw for bookmark ${bookmarkId}:`, response);
                
                // Check if response is wrapped in a data object
                if (response && response.data) {
                    console.log(`API Response unwrapped for bookmark ${bookmarkId}:`, response.data);
                    return response.data; // Return unwrapped data
                }
                
                return response;
            })
            .catch(error => {
                console.error(`API Error for bookmark ${bookmarkId}:`, error);
                throw error;
            });
    }

    getBookmarksByLogin(login:string) {
        if (isLocalDevelopment()) {
            console.log(`Using mock service for get bookmarks by login: ${login}`);
            return this.mockService.getBookmarks();
        }
        
        return super.get(`${this.urls.bookmarks}search/login/${login}`)
            .then(response => {
                console.log(`API Response raw for bookmarks by login ${login}:`, response);
                
                // Enhanced debugging to understand the API response structure
                console.log('API Response Type:', typeof response);
                console.log('API Response Keys:', response ? Object.keys(response as object) : 'No keys (response is null/undefined)');
                
                if (response && typeof response === 'object') {
                    const responseObj = response as Record<string, any>;
                    console.log('Has Items?', 'Items' in responseObj);
                    if ('Items' in responseObj) {
                        console.log('Items Type:', typeof responseObj.Items);
                        console.log('Items Length:', Array.isArray(responseObj.Items) ? responseObj.Items.length : 'Not an array');
                        if (Array.isArray(responseObj.Items) && responseObj.Items.length > 0) {
                            console.log('First Item Keys:', Object.keys(responseObj.Items[0] as object));
                        }
                    }
                    
                    if ('data' in responseObj) {
                        console.log(`API Response unwrapped for bookmarks by login ${login}:`, responseObj.data);
                        return responseObj.data; // Return unwrapped data
                    }
                }
                
                return response;
            })
            .catch(error => {
                console.error(`API Error for bookmarks by login ${login}:`, error);
                throw error;
            });
    }

    getBookmarksByTitle(title:string) {
        if (isLocalDevelopment()) {
            console.log(`Using mock service for get bookmarks by title: ${title}`);
            return this.mockService.getBookmarks().then(result => ({
                favorite_bookmarks: result.favorite_bookmarks.filter((b: any) => 
                    b.title.toLowerCase().includes(title.toLowerCase())
                )
            }));
        }
        
        return super.get(`${this.urls.bookmarks}search/title/${title}`)
            .then(response => {
                console.log(`API Response raw for bookmarks by title ${title}:`, response);
                
                // Enhanced debugging to understand the API response structure
                console.log('API Response Type:', typeof response);
                console.log('API Response Keys:', response ? Object.keys(response as object) : 'No keys (response is null/undefined)');
                
                if (response && typeof response === 'object') {
                    const responseObj = response as Record<string, any>;
                    console.log('Has Items?', 'Items' in responseObj);
                    if ('Items' in responseObj) {
                        console.log('Items Type:', typeof responseObj.Items);
                        console.log('Items Length:', Array.isArray(responseObj.Items) ? responseObj.Items.length : 'Not an array');
                        if (Array.isArray(responseObj.Items) && responseObj.Items.length > 0) {
                            console.log('First Item Keys:', Object.keys(responseObj.Items[0] as object));
                        }
                    }
                    
                    if ('data' in responseObj) {
                        console.log(`API Response unwrapped for bookmarks by title ${title}:`, responseObj.data);
                        return responseObj.data; // Return unwrapped data
                    }
                }
                
                return response;
            })
            .catch(error => {
                console.error(`API Error for bookmarks by title ${title}:`, error);
                throw error;
            });
    }

    addBookmark(title: string, url: string, description: string) {
        if (isLocalDevelopment()) {
            console.log(`Using mock service for add bookmark: ${title}`);
            return this.mockService.createBookmark({ title, url, description });
        }
        
        return super.post(
            `${this.urls.bookmarks}`,
            {"title":title, "url": url, "description": description}
        )
            .then(response => {
                console.log(`API Response raw for add bookmark ${title}:`, response);
                
                // Enhanced debugging to understand the API response structure
                console.log('API Response Type:', typeof response);
                console.log('API Response Keys:', response ? Object.keys(response as object) : 'No keys (response is null/undefined)');
                
                if (response && typeof response === 'object') {
                    const responseObj = response as Record<string, any>;
                    console.log('Has Attributes?', 'Attributes' in responseObj);
                    if ('Attributes' in responseObj) {
                        console.log('Attributes Type:', typeof responseObj.Attributes);
                        console.log('Attributes Keys:', Object.keys(responseObj.Attributes as object));
                    }
                    
                    if ('data' in responseObj) {
                        console.log(`API Response unwrapped for add bookmark ${title}:`, responseObj.data);
                        return responseObj.data; // Return unwrapped data
                    }
                }
                
                return response;
            })
            .catch(error => {
                console.error(`API Error for add bookmark ${title}:`, error);
                throw error;
            });
    }
    
    editBookmark(bookmarkId: string, title: string, description: string) {
        if (isLocalDevelopment()) {
            console.log(`Using mock service for edit bookmark: ${bookmarkId}`);
            return this.mockService.updateBookmark(bookmarkId, { title, description });
        }
        
        console.log(`edit bookmark using ${title} and ${description} for ${bookmarkId}`)
        return super.put(
            `${this.urls.bookmarks}${bookmarkId}`,
            {"title":title, "description": description}
        )
            .then(response => {
                console.log(`API Response raw for edit bookmark ${bookmarkId}:`, response);
                
                // Enhanced debugging to understand the API response structure
                console.log('API Response Type:', typeof response);
                console.log('API Response Keys:', response ? Object.keys(response as object) : 'No keys (response is null/undefined)');
                
                if (response && typeof response === 'object') {
                    const responseObj = response as Record<string, any>;
                    console.log('Has Attributes?', 'Attributes' in responseObj);
                    if ('Attributes' in responseObj) {
                        console.log('Attributes Type:', typeof responseObj.Attributes);
                        console.log('Attributes Keys:', Object.keys(responseObj.Attributes as object));
                    }
                    
                    if ('data' in responseObj) {
                        console.log(`API Response unwrapped for edit bookmark ${bookmarkId}:`, responseObj.data);
                        return responseObj.data; // Return unwrapped data
                    }
                }
                
                return response;
            })
            .catch(error => {
                console.error(`API Error for edit bookmark ${bookmarkId}:`, error);
                throw error;
            });
    }

    removeBookmark(bookmarkId: string) {
        if (isLocalDevelopment()) {
            console.log(`Using mock service for remove bookmark: ${bookmarkId}`);
            return this.mockService.deleteBookmark(bookmarkId);
        }
        
        return super.delete(`${this.urls.bookmarks}${bookmarkId}`)
            .then(response => {
                console.log(`API Response raw for remove bookmark ${bookmarkId}:`, response);
                
                // Enhanced debugging to understand the API response structure
                console.log('API Response Type:', typeof response);
                console.log('API Response Keys:', response ? Object.keys(response as object) : 'No keys (response is null/undefined)');
                
                if (response && typeof response === 'object') {
                    const responseObj = response as Record<string, any>;
                    console.log('Has Attributes?', 'Attributes' in responseObj);
                    if ('Attributes' in responseObj) {
                        console.log('Attributes Type:', typeof responseObj.Attributes);
                        console.log('Attributes Keys:', Object.keys(responseObj.Attributes as object));
                    }
                    
                    if ('data' in responseObj) {
                        console.log(`API Response unwrapped for remove bookmark ${bookmarkId}:`, responseObj.data);
                        return responseObj.data; // Return unwrapped data
                    }
                }
                
                return response;
            })
            .catch(error => {
                console.error(`API Error for remove bookmark ${bookmarkId}:`, error);
                throw error;
            });
    }
}
